/* eslint-disable @typescript-eslint/no-explicit-any */
import { openMailto } from "@/utils/mailto";
import Hero from "../components/hero";
import Help from "../components/help";
import heroImage from "@assets/images/investor-hero.svg";
import safeIcon from "@assets/icons/safeIcon.svg";
import documentIcon from "@assets/icons/document.svg";
import downloadIcon from "@assets/icons/download-arrow.svg";
import tickIcon from "@assets/icons/tick-circle.svg";
import Button from "../components/button";
import InputField from "../components/inputField";
import { useState } from "react";

const heroData = {
  title: "Governance, Compliance, and Shareholder Transparency at GAZ",
  description:
    "GAS Integrated Solutions L.L.C. maintains clear governance practices, disciplined operational oversight, and full regulatory compliance. This page provides investors, stakeholders, and partners with access to key governance principles, disclosures, and corporate documents.",
  image: heroImage,
  footerText:
    "We provides safe, certified, and efficient gas connections across the UAE delivered and activated quickly by our licensed technicians",
  footerButtonText: "Talk to Our Team",
  footerButtonOnclick: () =>
    openMailto({
      subject: "Governance Compliance and Shareholder Transparency Inquiry",
    }),
  footerIcon: <img src={safeIcon} alt="" className="w-7 h-7" />,
};

const governancePhilosophyPoints = [
  "ESMA Commercial Licenses (x4)",
  "Dubai Civil Defense Approval (x4)",
  "ISO 9001:2000 safety operations",
  "ISO 14001 (Environmental Management)",
  "ISO 45001 (Occupational Health & Safety)",
  "Central Electricity and Gas Distribution Policy",
];

const corePrinciples = [
  {
    title: "Transparency",
    desc: "Clear reporting, consistent disclosures, and accessible documentation.",
  },
  {
    title: "Integrity",
    desc: "EEthical conduct in operations, procurement, and customer interaction.",
  },
  {
    title: "Accountability",
    desc: "Responsibilities defined at executive, operational, and departmental levels.",
  },
  {
    title: "Compliance",
    desc: "All activities governed by UAE laws, safety codes, and financial standards.",
  },
  {
    title: "Risk Management",
    desc: "Proactive identification, mitigation, and monitoring of operational risks.",
  },
];

const keyDocumentsLeft = [
  {
    title: "Company Registration & Legal Documents",
    desc: "5 Documents",
  },
  {
    title: "Governance & Compliance Documents",
    desc: "8 Documents",
  },
  {
    title: "Financial & Audit Documentation",
    desc: "5 Documents (Only shared with authorized investors or regulators)",
  },
  {
    title: "Operational & Safety Governance",
    desc: "6 Documents",
  },
];

const keyDocumentsRight = [
  "Trade License (SBTC Zone)",
  "Shareholder & Structure Certificate",
  "Memorandum of Association (MOA)",
  "Board Resolutions & Authority",
  "VAT Registration Certificate",
];

const boardStructurePoints = [
  "GAZ operates under a leadership structure aligned with Contra Group’s governance:",
  "Managing Director / General Manager",
  "Operations & Technical Leadership Team",
  "Finance & Compliance Team",
  "HSE & Risk Management Officers",
];

const decisionMakingPoints = [
  "Governance decisions follow:",
  "Delegation of Authority Matrix",
  "Financial Approval Limits",
  "Procurement & Vendor Evaluation Criteria",
  "Internal Compliance Audit Process",
];

const riskPolicyPoints = [
  "Safety risks (high priority)",
  "Operational risks (network, field operations, contractors)",
  "Financial risks (billing, receivables, payment gateway, VAT)",
  "Cyber risks (data privacy, system outages, cybersecurity threats)",
  "Regulatory risks (DCD, ESMA, municipality, PDPL)",
];

const riskApproachColumns = [
  {
    title: "Identify",
    desc: "Monitor operational, financial, and safety exposures",
  },
  {
    title: "Assess",
    desc: "Severity, likelihood, and timeline",
  },
  { title: "Mitigate", desc: "Preventive and contingency plans" },
  { title: "Monitor", desc: "Preventive and contingency plans" },
  {
    title: "Report",
    desc: "Escalation to management and stakeholders",
  },
];

const sustainabilityGovernancePoints = [
  "ESG reporting aligned to UAE Vision 2030",
  "Annual sustainability report available to shareholders",
  "Board-level oversight of environmental commitments",
  "Social responsibility investments in community safety education",
];

const investorAccessCards = [
  "Financial reports",
  "Governance documents",
  "Operational dashboards",
  "Audit summaries",
];

// ── COMPONENT ─────────────────────────────────────────────────────────────────

function InvestorGovernance() {
  return (
    <div className="bg-white">
      <Hero data={heroData} />

      {/* ── OUR GOVERNANCE PHILOSOPHY ─────────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <div className="flex flex-col justify-between gap-10 xl:flex-row">
            {/* Left */}
            <div className="xl:max-w-[480px]">
              <h2 className="text-gis-dark-blue text-[24px] md:text-[36px] lg:text-[56px] font-semibold">
                Our Governance Philosophy
              </h2>
              <p className="mt-4 text-[14px] md:text-base text-[#62636C] leading-relaxed">
                GAZ follows a governance model aligned with industry leading
                standards and regulatory frameworks
              </p>
            </div>
            {/* Right */}
            <div className="xl:max-w-[500px] w-full">
              <ul className="space-y-2">
                {governancePhilosophyPoints.map((point, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <img src={tickIcon} alt="tick icon" />
                    <span className="text-[14px] md:text-sm lg:text-base text-[#0A0E2EBF]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Core Principles */}
          <div className="mt-10 md:mt-16 p-4 md:p-8 bg-[#F1F1F1] rounded-[12px] md:rounded-[24px]">
            <h3 className="mb-6 text-sm font-semibold text-gis-dark-blue md:text-xl lg:text-[40px]">
              Core Principles
            </h3>
            <div className="grid gap-4 md:grid-cols-3 2xl:grid-cols-5">
              {corePrinciples.map((item, index) => (
                <div
                  key={index}
                  className="p-3 bg-white rounded-md md:rounded-lg md:p-6"
                >
                  <h4 className="text-[#090D2C] font-semibold text-xs md:text-sm lg:text-lg mb-2">
                    {item.title}
                  </h4>
                  <p className="text-[14px] md:text-sm text-[#0A0E2EBF]">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── KEY CORPORATE DOCUMENTS ───────────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Key Corporate Documents
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            Access our comprehensive library of governance and compliance
            documentation
          </p>

          <div className="grid gap-4 mt-10 lg:mt-16 md:grid-cols-2">
            {/* Left column — numbered list */}
            <div className="space-y-4">
              {keyDocumentsLeft.map((doc, index) => (
                <article
                  key={index}
                  className="p-4 md:p-6 bg-white border border-[#62636C33] rounded-[12px] md:rounded-[24px] flex items-start gap-4"
                >
                  <div className="bg-black size-9 md:size-[56px] grid place-content-center rounded-[12px] flex-shrink-0">
                    <span className="text-xs font-semibold text-white md:text-base lg:text-lg">
                      {`0${index + 1}`}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-lg">
                      {doc.title}
                    </h3>
                    <p className="mt-1 text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                      {doc.desc}
                    </p>
                  </div>
                </article>
              ))}
            </div>

            {/* Right column — highlighted red cards */}
            <div className="p-6 space-y-4 rounded-md bg-gis-red md:rounded-lg">
              {keyDocumentsRight.map((doc, index) => (
                <article
                  key={index}
                  className="p-4 bg-white/90 rounded-[12px] md:rounded-[16px] flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-md">
                      <img
                        src={documentIcon}
                        alt="document"
                        className="flex-shrink-0 size-5 md:size-6 invert"
                      />
                    </div>
                    <h3 className="text-sm font-normal text-[#1E1F24] md:text-base lg:text-lg">
                      {doc}
                    </h3>
                  </div>
                  <button className="flex-shrink-0">
                    <img
                      src={downloadIcon}
                      alt="download"
                      className="size-5 md:size-6 invert"
                    />
                  </button>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── BOARD & EXECUTIVE OVERSIGHT ───────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Board &amp; Executive Oversight
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            GAZ operates under a leadership structure aligned with Contra
            Group's governance
          </p>

          <div className="grid gap-4 mt-10 lg:mt-16 md:grid-cols-2">
            <article className="p-4 md:p-8 border border-[#62636C33] rounded-[12px] md:rounded-[24px]">
              <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl mb-4">
                Board Structure
              </h3>
              <ul className="space-y-2">
                {boardStructurePoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-2 rounded-full bg-[#62636C]" />
                    <span className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="p-4 md:p-8 border border-[#62636C33] rounded-[12px] md:rounded-[24px]">
              <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl mb-4">
                Decision-Making Framework
              </h3>
              <ul className="space-y-2">
                {decisionMakingPoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-2 rounded-full bg-[#62636C]" />
                    <span className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* ── RISK MANAGEMENT POLICY ────────────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <div className="flex flex-col items-start justify-between gap-10 xl:flex-row">
            {/* Left */}
            <div className="xl:max-w-[480px]">
              <h2 className="text-gis-dark-blue text-[24px] md:text-[36px] lg:text-[56px] font-semibold">
                Risk Management Policy
              </h2>
              <p className="mt-4 text-[14px] md:text-base text-[#62636C] leading-relaxed">
                GAZ implements a structured risk framework addressing all
                operational aspects
              </p>
            </div>

            <div className="xl:max-w-[640px] w-full">
              <ul className="space-y-2">
                {riskPolicyPoints.map((point, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <img src={tickIcon} alt="tick icon" />
                    <span className="text-[14px] md:text-sm lg:text-base text-[#0A0E2EBF]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 md:mt-16 p-4 md:p-8 bg-[#F1F1F1] rounded-[12px] md:rounded-[24px]">
            <h3 className="mb-6 text-sm font-semibold text-gis-dark-blue md:text-xl lg:text-[40px]">
              Our Risk Approach
            </h3>
            <div className="grid gap-4 md:grid-cols-3 2xl:grid-cols-5">
              {riskApproachColumns.map((item, index) => (
                <div
                  key={index}
                  className="p-3 bg-white rounded-md md:rounded-lg md:p-6"
                >
                  <h4 className="text-[#090D2C] font-semibold text-xs md:text-sm lg:text-lg mb-4">
                    {item.title}
                  </h4>
                  <p className="text-[14px] md:text-sm text-[#0A0E2EBF]">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SUSTAINABILITY & ENVIRONMENTAL GOVERNANCE ─────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <div className="flex flex-col items-start justify-between gap-10 xl:items-center xl:flex-row">
            {/* Left */}
            <div className="xl:max-w-[560px]">
              <h2 className="text-gis-dark-blue text-[24px] md:text-[36px] lg:text-[60px] font-semibold">
                Sustainability &amp; Environmental Governance
              </h2>
              <p className="mt-4 text-[14px] md:text-base text-[#62636C] leading-relaxed">
                Where applicable, GAZ promotes environmentally responsible
                practices throughout our operations, contributing to a
                sustainable future for the UAE.
              </p>

              <div className="text-[12px] md:text-xs lg:text-sm text-[#62636C] mt-8 px-3 md:px-5 py-3 bg-[#F1F1F1] rounded-lg">
                "A sustainability statement can be added once internal data is
                available."
              </div>
            </div>

            {/* Right */}
            <div className="xl:max-w-[640px] w-full">
              <p className="text-[14px] md:text-xs lg:text-sm text-[#62636C] mb-2">
                Where applicable, GAZ promotes:
              </p>
              <ul className="space-y-2 md:space-y-5">
                {sustainabilityGovernancePoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-3 rounded-full bg-gis-red" />
                    <span className="text-[14px] md:text-xs lg:text-sm text-[#62636C]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── AUTHORIZED INVESTOR ACCESS ────────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto flex flex-col xl:flex-row gap-10 justify-between items-start xl:items-center">
          {/* Left */}
          <div className="xl:max-w-[400px]">
            <h2 className="text-[#122D63] text-[24px] md:text-[36px] lg:text-[56px] font-semibold">
              Authorized Investor Access
            </h2>
            <p className="mt-4 text-[14px] md:text-base text-[#62636C] leading-relaxed">
              Authorized investors, lenders, or partners may request access to
              comprehensive documentation and real-time operational insights.
            </p>
            <Button
              title="Request Investor Access"
              onClick={() => openMailto({ subject: "Investor Access Request" })}
              className="mt-6 md:mt-12"
            />
          </div>

          {/* Right — 2x2 grid */}
          <div className="xl:max-w-[700px] w-full grid grid-cols-2 gap-4">
            {investorAccessCards.map((data, index) => (
              <article
                key={index}
                className="p-4 md:p-8 bg-white rounded-[12px] md:rounded-[24px] flex flex-col"
              >
                <span className="p-2 text-xs font-semibold text-white bg-black rounded-md md:text-sm min-w-[40px] w-fit text-center block">
                  {`0${index + 1}`}
                </span>
                <h3 className="pt-2 mt-auto text-sm font-semibold md:text-base lg:text-xl">
                  {data}
                </h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── REQUESTS & VERIFICATION ───────────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Requests &amp; Verification
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4 max-w-[800px] mx-auto">
            Investors or stakeholders may request official copies of the above
            documents.
          </p>

          <RequestVerificationForm />
        </div>
      </section>

      {/* ── HELP CTA ──────────────────────────────────────────────────── */}
      <Help title="Let’s Build a Safer Energy Future Together " />
    </div>
  );
}

const RequestVerificationForm = () => {
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);

  const handleCheckboxChange = (doc: string) => {
    setSelectedDocs((prev) =>
      prev.includes(doc) ? prev.filter((d) => d !== doc) : [...prev, doc]
    );
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    const fullName = formData.get("fullName");
    const email = formData.get("email");
    const organization = formData.get("organization");
    const purpose = formData.get("purpose");

    const documents =
      selectedDocs.length > 0
        ? selectedDocs.map((doc) => `• ${doc}`).join("\n")
        : "None selected";

    const body = `Hello,

      I would like to request access to the following documents.

      Full Name: ${fullName}
      Email Address: ${email}
      Organization: ${organization}
      Purpose of Request: ${purpose}

      Requested Document(s):
      ${documents}

      Thank you.`;

    openMailto({
      subject: `Document Access Request - ${organization}`,
      body,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-6 md:mt-12 bg-white rounded-[12px] md:rounded-[24px] px-5 md:px-10 lg:px-[100px] py-5 md:py-10 lg:py-20 space-y-6 md:space-y-8 w-full"
    >
      <div className="flex flex-col gap-6 md:flex-row">
        <InputField
          label="Full Name"
          placeholder="Enter your full name"
          name="fullName"
          required
        />

        <InputField
          label="Email Address"
          placeholder="Enter your email address"
          type="email"
          name="email"
          required
        />
      </div>

      <div className="flex flex-col gap-6 md:flex-row">
        <InputField
          label="Organization"
          placeholder="Enter your organization name"
          name="organization"
          required
        />

        <InputField
          label="Purpose of Request"
          placeholder="Enter the purpose of your request"
          name="purpose"
          required
        />
      </div>

      <div>
        <span className="text-sm md:text-lg lg:text-xl text-[#0A0E2EBF] font-medium block mb-2 md:mb-3">
          Documents Required (multi-select)
        </span>

        <div className="grid gap-3 md:gap-6 md:grid-cols-2">
          {investorAccessCards.map((data, index) => (
            <div key={index} className="flex gap-2 md:items-center">
              <input
                type="checkbox"
                className="flex-shrink-0 size-5 md:size-7"
                id={data}
                checked={selectedDocs.includes(data)}
                onChange={() => handleCheckboxChange(data)}
              />

              <label
                htmlFor={data}
                className="text-[#0A0E2EBF] text-sm md:text-lg lg:text-xl"
              >
                {data}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center pt-5">
        <Button title="Submit Request" type="submit" />
      </div>
    </form>
  );
};

export default InvestorGovernance;
